<template>
  <div class="ism-tbl-card">

    <!-- Header -->
    <div class="ism-tbl-header">
      <div>
        <div class="ism-card-title">FAQs</div>
        <div class="ism-card-subtitle">Manage frequently asked questions shown to society members.</div>
      </div>
      <div class="ism-tbl-header-actions">
        <div class="ism-search">
          <v-icon size="16" color="#94a3b8">mdi-magnify</v-icon>
          <input v-model="search" placeholder="Search FAQs..." @input="page = 1" />
          <v-icon v-if="search" size="14" color="#94a3b8" style="cursor:pointer" @click="search=''; page=1">mdi-close</v-icon>
        </div>
        <button class="btn-accent" @click="openAdd">
          <v-icon size="16" color="#fff">mdi-plus</v-icon>
          Add FAQ
        </button>
      </div>
    </div>

    <!-- Table -->
    <div class="ism-tbl-wrapper">
      <table class="ism-table">
        <thead class="ism-thead">
          <tr>
            <th style="width:44px;padding-left:20px;"></th>
            <th style="width:56px;">No</th>
            <th>Question</th>
            <th>Answer</th>
            <th style="width:160px;">Category</th>
            <th style="width:120px;">Date</th>
            <th style="width:110px;">Actions</th>
          </tr>
        </thead>

        <!-- Loading skeleton -->
        <tbody v-if="loading">
          <tr v-for="i in perPage" :key="i">
            <td v-for="j in 7" :key="j" style="padding:14px;">
              <div class="ism-shimmer" style="height:12px;border-radius:4px;"></div>
            </td>
          </tr>
        </tbody>

        <!-- Empty -->
        <tbody v-else-if="!loading && filteredFaqs.length === 0">
          <tr>
            <td colspan="7" class="ism-empty-cell">
              <div class="ism-empty-state">
                <v-icon size="44" color="#cbd5e1">mdi-database-off-outline</v-icon>
                <p class="ism-empty-text">No FAQs found</p>
              </div>
            </td>
          </tr>
        </tbody>

        <!-- Draggable rows (current page only) -->
        <draggable
          v-else
          v-model="currentPageItems"
          tag="tbody"
          handle=".drag-handle"
          animation="200"
          ghost-class="drag-ghost"
          @end="onSortEnd"
        >
          <tr v-for="(item, idx) in currentPageItems" :key="item._id || idx" class="ism-tr">
            <td style="padding-left:20px;text-align:center;">
              <v-icon size="18" class="drag-handle" color="#cbd5e1">mdi-drag-vertical</v-icon>
            </td>
            <td>{{ (page - 1) * perPage + idx + 1 }}</td>
            <td><div class="cell-clamp">{{ item.question }}</div></td>
            <td><div class="cell-clamp text-secondary">{{ item.answer }}</div></td>
            <td>
              <span v-if="item.category" class="ism-category-badge">{{ item.category.name }}</span>
              <span v-else class="ism-no-category">—</span>
            </td>
            <td><span class="ism-date-text">{{ formatDate(item.created_at) }}</span></td>
            <td>
              <div style="display:flex;gap:8px;align-items:center;">
                <PremiumActionButton icon="mdi-pencil-outline" label="Edit" @click="openEdit(item)" />
                <PremiumActionButton icon="mdi-delete-outline" label="Delete" variant="danger" @click="confirmDelete(item._id)" />
              </div>
            </td>
          </tr>
        </draggable>
      </table>
    </div>

    <!-- Pagination footer -->
    <div class="ism-tbl-footer" v-if="!loading && filteredFaqs.length > 0">
      <div class="ism-pag-container">
        <div class="ism-rpp-wrap">
          <span class="ism-rpp-label">Rows per page</span>
          <v-menu offset-y nudge-bottom="4">
            <template v-slot:activator="{ on, attrs }">
              <div class="ism-rpp-select" v-bind="attrs" v-on="on">
                <span>{{ perPage }}</span>
                <v-icon size="14" color="#64748b">mdi-chevron-down</v-icon>
              </div>
            </template>
            <div class="ism-rpp-list">
              <div
                v-for="opt in [10, 25, 50, 100]" :key="opt"
                class="ism-rpp-item"
                :class="{ 'ism-rpp-item--active': perPage === opt }"
                @click="perPage = opt; page = 1"
              >{{ opt }}</div>
            </div>
          </v-menu>
        </div>
        <span class="ism-row-range">{{ firstRow }}–{{ lastRow }} of {{ filteredFaqs.length }}</span>
        <div class="ism-pag-btns">
          <button class="ism-pag-btn" :disabled="page === 1" @click="page = 1"><v-icon size="14">mdi-chevron-double-left</v-icon></button>
          <button class="ism-pag-btn" :disabled="page === 1" @click="page--"><v-icon size="14">mdi-chevron-left</v-icon></button>
          <button class="ism-pag-btn" :disabled="page === totalPages" @click="page++"><v-icon size="14">mdi-chevron-right</v-icon></button>
          <button class="ism-pag-btn" :disabled="page === totalPages" @click="page = totalPages"><v-icon size="14">mdi-chevron-double-right</v-icon></button>
        </div>
      </div>
    </div>

    <!-- Delete Confirm Modal -->
    <ConfirmModal
      :isOpen="deleteDialog"
      type="danger"
      title="Delete FAQ"
      message="Are you sure you want to delete this FAQ?"
      confirmText="Yes, Delete"
      cancelText="Cancel"
      @confirm="deleteFaq"
      @cancel="deleteDialog = false"
    />

    <!-- Add/Edit Modal -->
    <PremiumModalForm
      v-model="faqDialog"
      :title="isEdit ? 'Edit FAQ' : 'Add FAQ'"
      :submitText="isEdit ? 'Update FAQ' : 'Save FAQ'"
      :loading="saving"
      @submit="saveFaq"
    >
      <div style="display:flex;flex-direction:column;gap:10px;">
        <PremiumInput
          v-model="faq.category_id"
          name="category_id"
          label="Category"
          is-select
          :items="categoryOptions"
          item-text="label"
          item-value="value"
          placeholder="Select category (optional)"
          clearable
        />
        <PremiumInput
          v-model="faq.question"
          name="question"
          label="Question *"
          placeholder="Enter question"
          is-textarea
          rows="2"
          v-validate="'required'"
          :error="errors.first('question') || serverSideErrors.question"
        />
        <PremiumInput
          v-model="faq.answer"
          name="answer"
          label="Answer *"
          placeholder="Enter answer"
          is-textarea
          rows="4"
          v-validate="'required'"
          :error="errors.first('answer') || serverSideErrors.answer"
        />
      </div>
    </PremiumModalForm>
  </div>
</template>

<script>
import { mapMutations } from 'vuex';
import moment from 'moment';
import draggable from 'vuedraggable';
import ConfirmModal from '../../common/ConfirmModal.vue';
import PremiumActionButton from '../../common/PremiumActionButton.vue';
import PremiumModalForm from '../../common/PremiumModalForm.vue';
import PremiumInput from '../../common/PremiumInput.vue';

export default {
  name: 'FaqList',
  components: { draggable, ConfirmModal, PremiumActionButton, PremiumModalForm, PremiumInput },

  data() {
    return {
      faqs:          [],
      sortableFaqs:  [],
      categories:    [],
      loading:       false,
      saving:        false,
      deleteDialog:  false,
      faqDialog:     false,
      isEdit:        false,
      deletingId:    null,
      editId:        null,
      search:        '',
      page:          1,
      perPage:       10,
      faq: { question: '', answer: '', category_id: null },
      serverSideErrors: {},
    };
  },

  computed: {
    filteredFaqs() {
      if (!this.search) return this.sortableFaqs;
      const q = this.search.toLowerCase();
      return this.sortableFaqs.filter(f =>
        (f.question || '').toLowerCase().includes(q) ||
        (f.answer   || '').toLowerCase().includes(q) ||
        (f.category && f.category.name && f.category.name.toLowerCase().includes(q))
      );
    },
    totalPages() { return Math.max(1, Math.ceil(this.filteredFaqs.length / this.perPage)); },
    firstRow()   { return this.filteredFaqs.length === 0 ? 0 : (this.page - 1) * this.perPage + 1; },
    lastRow()    { return Math.min(this.page * this.perPage, this.filteredFaqs.length); },

    // Two-way binding for draggable — current page slice of filteredFaqs
    currentPageItems: {
      get() {
        const start = (this.page - 1) * this.perPage;
        return this.filteredFaqs.slice(start, start + this.perPage);
      },
      set(newPageItems) {
        // Splice the reordered page back into sortableFaqs
        const start = (this.page - 1) * this.perPage;
        const updated = [...this.sortableFaqs];
        // Replace only the current page slice
        updated.splice(start, newPageItems.length, ...newPageItems);
        this.sortableFaqs = updated;
      },
    },

    categoryOptions() {
      return this.categories.map(c => ({ label: c.name, value: c._id || c.id }));
    },
  },

  watch: {
    filteredFaqs() { if (this.page > this.totalPages) this.page = this.totalPages; },
  },

  methods: {
    ...mapMutations('alert', ['setAlert']),

    formatDate(d) { return d ? moment(d).format('DD MMM, YYYY') : '—'; },

    getFaqs() {
      this.loading = true;
      this.$axios.get('superadmin/faqs').then(res => {
        this.loading = false;
        if (res.data.status) {
          this.faqs = res.data.faqs;
          this.sortableFaqs = [...res.data.faqs];
        } else {
          this.setAlert({ showAlert: true, variant: 'red accent-3', timeout: 3000, message: res.data.message });
        }
      }).catch(() => { this.loading = false; });
    },

    getCategories() {
      this.$axios.get('superadmin/faq-categories').then(res => {
        if (res.data.status) this.categories = res.data.categories;
      }).catch(() => {});
    },

    onSortEnd() {
      // Build sort_data from full sortableFaqs array
      const sort_data = this.sortableFaqs.map((item, idx) => ({
        id: item._id || item.id,
        sort_order: idx + 1,
      }));
      this.$axios.put('superadmin/faqs/sort', { sort_data }).then(res => {
        if (res.data.status) {
          this.setAlert({ showAlert: true, variant: 'green accent-4', timeout: 2000, message: 'Sort order saved' });
        } else {
          this.setAlert({ showAlert: true, variant: 'red accent-3', timeout: 3000, message: res.data.message });
          this.sortableFaqs = [...this.faqs];
        }
      }).catch(() => { this.sortableFaqs = [...this.faqs]; });
    },

    confirmDelete(id) { this.deletingId = id; this.deleteDialog = true; },

    deleteFaq() {
      this.deleteDialog = false;
      this.$axios.delete(`superadmin/faqs/${this.deletingId}`).then(res => {
        if (res.data.status) {
          this.getFaqs();
          this.setAlert({ showAlert: true, variant: 'green accent-4', timeout: 3000, message: res.data.message });
        } else {
          this.setAlert({ showAlert: true, variant: 'red accent-3', timeout: 3000, message: res.data.message });
        }
      });
    },

    openAdd() {
      this.isEdit = false;
      this.faq = { question: '', answer: '', category_id: null };
      this.serverSideErrors = {};
      this.$validator.reset();
      this.faqDialog = true;
    },

    openEdit(item) {
      this.isEdit = true;
      this.editId = item._id;
      this.faq = {
        question:    item.question,
        answer:      item.answer,
        category_id: item.category_id ? String(item.category_id) : null,
      };
      this.serverSideErrors = {};
      this.$validator.reset();
      this.faqDialog = true;
    },

    saveFaq() {
      this.$validator.validateAll().then(valid => {
        if (!valid) return;
        this.saving = true;
        const method = this.isEdit ? 'put' : 'post';
        const url    = this.isEdit ? `superadmin/faqs/${this.editId}` : 'superadmin/faqs';
        this.$axios[method](url, this.faq).then(res => {
          this.saving = false;
          if (res.data.status) {
            this.faqDialog = false;
            this.getFaqs();
            this.setAlert({ showAlert: true, variant: 'green accent-4', timeout: 3000, message: res.data.message });
          } else {
            if (res.data.errors) res.data.errors.forEach(e => { this.serverSideErrors[e.field] = e.message; });
            else this.setAlert({ showAlert: true, variant: 'red accent-3', timeout: 3000, message: res.data.message });
          }
        }).catch(() => { this.saving = false; });
      });
    },
  },

  mounted() {
    this.getFaqs();
    this.getCategories();
  },
};
</script>

<style scoped>
.ism-tbl-card { background: var(--card-bg,#fff); border: 1px solid var(--border-color,rgba(0,0,0,.07)); border-radius: 12px; overflow: hidden; box-shadow: 0 1px 4px rgba(0,0,0,.06); }
.ism-tbl-header { padding: 18px 24px; border-bottom: 1px solid var(--border-color,rgba(0,0,0,.06)); display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap; }
.ism-card-title { font-size: 15px; font-weight: 800; color: var(--text-primary,#1e293b); letter-spacing: -.02em; }
.ism-card-subtitle { font-size: 12px; color: var(--text-secondary,#64748b); margin-top: 2px; }
.ism-tbl-header-actions { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.ism-search { display: flex; align-items: center; gap: 8px; background: var(--bg-secondary,#fff); border: 1px solid var(--border-color,rgba(0,0,0,.1)); border-radius: 8px; padding: 8px 14px; min-width: 240px; height: 40px; }
.ism-search:focus-within { border-color: var(--accent,#F26522); }
.ism-search input { border: none; outline: none; background: transparent; font-size: 13px; width: 100%; color: var(--text-primary,#1e293b); }
.ism-search input::placeholder { color: #94a3b8; }
.btn-accent { display: inline-flex; align-items: center; gap: 6px; background: linear-gradient(135deg,#F26522,#C94F10); color: #fff; border: none; border-radius: 8px; font-size: 13px; font-weight: 600; padding: 8px 16px; cursor: pointer; transition: all .2s; box-shadow: 0 4px 12px rgba(242,101,34,.25); }
.btn-accent:hover { transform: translateY(-1px); }
.ism-tbl-wrapper { overflow-x: auto; }
.ism-table { width: 100%; border-collapse: separate; border-spacing: 0; font-family: "Inter",sans-serif; }
.ism-thead { background: var(--bg-primary,#f5f6f7); }
.ism-thead th { height: 44px; font-size: 13px; font-weight: 700; text-transform: capitalize; color: var(--text-secondary,#64748b); border-bottom: 1px solid var(--border-color,rgba(0,0,0,.07)); padding: 10px 14px; white-space: nowrap; text-align: left; }
.ism-table tbody tr td { height: 52px; font-size: 13.5px; color: var(--text-primary,#1e293b); padding: 5px 14px; border-bottom: 1px solid var(--border-color,rgba(0,0,0,.07)); vertical-align: middle; background: var(--card-bg,#fff); }
.ism-table tbody tr:hover td { background: rgba(242,101,34,.04) !important; }
.ism-table tbody tr:last-child td { border-bottom: none; }
.ism-empty-cell { border-bottom: none !important; }
.ism-empty-state { display: flex; flex-direction: column; align-items: center; gap: 10px; padding: 48px 24px; }
.ism-empty-text { font-size: 14px; color: var(--text-secondary,#64748b); margin: 0; }
.ism-shimmer { background: linear-gradient(90deg,#f1f5f9 25%,rgba(0,0,0,.07) 50%,#f1f5f9 75%); background-size: 200% 100%; animation: shimmer 1.5s infinite linear; }
@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
.drag-handle { cursor: grab !important; }
.drag-handle:hover { color: #475569 !important; }
.drag-ghost td { background: rgba(242,101,34,.08) !important; opacity: .7; }
.cell-clamp { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; max-width: 280px; font-size: 13.5px; line-height: 1.5; }
.text-secondary { color: var(--text-secondary,#64748b) !important; }
.ism-date-text { font-size: 12.5px; color: var(--text-secondary,#64748b); white-space: nowrap; }
.ism-category-badge { display: inline-flex; align-items: center; padding: 3px 10px; border-radius: 20px; font-size: 12px; font-weight: 600; background: rgba(242,101,34,.1); color: var(--accent,#f26522); white-space: nowrap; }
.ism-no-category { color: #94a3b8; font-size: 13px; }
/* Footer pagination */
.ism-tbl-footer { padding: 16px 24px; border-top: 1px solid var(--border-color,rgba(0,0,0,.07)); display: flex; justify-content: flex-end; }
.ism-pag-container { display: flex; align-items: center; gap: 24px; flex-wrap: wrap; }
.ism-rpp-wrap { display: flex; align-items: center; gap: 10px; }
.ism-rpp-label { font-size: 13px; font-weight: 600; color: var(--text-secondary,#64748b); white-space: nowrap; }
.ism-rpp-select { display: inline-flex; align-items: center; justify-content: space-between; width: 65px; background: transparent; border: 1px solid var(--border-color,rgba(0,0,0,.1)); border-radius: 8px; color: var(--text-primary,#1e293b); font-size: 13px; font-weight: 600; padding: 6px 10px; cursor: pointer; user-select: none; }
.ism-rpp-list { background: var(--card-bg,#fff); display: flex; flex-direction: column; min-width: 65px; }
.ism-rpp-item { padding: 8px 16px; font-size: 13px; font-weight: 500; color: var(--text-primary,#1e293b); cursor: pointer; transition: background .2s; text-align: center; }
.ism-rpp-item:hover { background: var(--bg-primary,#f1f5f9); }
.ism-rpp-item--active { background: rgba(242,101,34,.1); color: var(--accent,#F26522); font-weight: 700; }
.ism-row-range { font-size: 12.5px; color: var(--text-secondary,#64748b); font-weight: 600; white-space: nowrap; }
.ism-pag-btns { display: flex; align-items: center; gap: 4px; }
.ism-pag-btn { width: 32px; height: 32px; border: none; background: transparent; color: var(--text-secondary,#64748b); border-radius: 8px; display: inline-flex; align-items: center; justify-content: center; cursor: pointer; transition: all .2s; }
.ism-pag-btn:hover:not(:disabled) { background: rgba(100,116,139,.15); }
.ism-pag-btn:disabled { opacity: .25; cursor: not-allowed; }
</style>
